/**
 * AtlasAdaptiveQualityModule — ADAPTIVE RENDER SCALE (perf frontier 2026-09-08,
 * item 9): a frame-time governor (lib/frame_governor.ts) watches the frame
 * interval and moves the FSR upscale ratio LIVE through the post module —
 * down one preset step when the machine cannot hold its refresh rate for
 * 2 s, probed back up after a steady stretch. The lever is the scene pass
 * resolution behind the EASU kernel, so the picture keeps its output size
 * and the tier's own ratio is the floor; nothing else in the tier changes.
 *
 * Off when the tier's `adaptive` flag is false, when the tier has no upscale
 * stage (nothing to move), in XR, or while the loading gate is up.
 * Levers: window.__adaptive() — governor state; localStorage
 * 'atlas:adaptive' = 'off' disables it without touching the tier.
 */
import type { App, IModule } from 'enginex';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { activePreset, UPSCALE_RATIO } from '../lib/quality_tiers.js';
import { FrameGovernor } from '../lib/frame_governor.js';
import type { PostModule } from './PostModule.js';

export class AtlasAdaptiveQualityModule implements IModule {
  readonly name = 'AtlasAdaptiveQuality';
  private gov = new FrameGovernor();
  private lastT = -1;
  private base = 1;
  private applied = 1;
  private changes: Array<{ t: number; dir: string; scale: number; median: number }> = [];
  private active = false;

  constructor(private readonly post: PostModule) {}

  init(app: App): void {
    app.world.addSystem(makeSystem('AtlasAdaptiveQuality', Phase.UI, 90, () => this.tick(app)));
    bus.on('atlas:quality-changed', () => this.resync());
    this.resync();
    (globalThis as unknown as { __adaptive?: () => unknown }).__adaptive = () => ({
      active: this.active, base: this.base, scale: this.gov.scale, applied: this.applied, live: this.post.liveRenderScale,
      refreshMs: this.gov.refreshInterval, budgetMs: this.gov.budgetMs, medianMs: this.gov.medianMs, changes: this.changes.slice(-12),
    });
  }

  private optCheckedAt = -1e9;
  private optedOutNow = false;
  static optedOut(): boolean {
    try { return localStorage.getItem('atlas:adaptive') === 'off'; } catch { return false; }
  }

  private resync(): void {
    const p = activePreset();
    this.base = UPSCALE_RATIO[p.upscale ?? 'off'] ?? 1;
    this.gov.reset(this.base, 2.0);
    this.applied = this.base;
    this.lastT = -1;
  }

  private tick(app: App): void {
    const now = performance.now();
    const dt = this.lastT < 0 ? 0 : now - this.lastT;
    this.lastT = now;
    const p = activePreset();
    const xr = (app.renderer.native as unknown as { xr?: { isPresenting?: boolean } }).xr?.isPresenting ?? false;
    if (now - this.optCheckedAt > 1000) { this.optCheckedAt = now; this.optedOutNow = AtlasAdaptiveQualityModule.optedOut(); } // a synchronous localStorage read — 1 Hz, not per frame
    this.active = !!p.adaptive && (p.upscale ?? 'off') !== 'off' && !xr && !this.optedOutNow && atlas.currentWorldId !== null && atlas.loadingGate === false;
    if (!this.active || dt <= 0) return;
    const d = this.gov.push(dt, now);
    if (!d) return;
    if (this.post.setRenderScaleLive(d.scale)) {
      this.applied = d.scale;
      this.changes.push({ t: Math.round(now), dir: d.dir, scale: d.scale, median: Math.round(this.gov.medianMs * 10) / 10 });
      if (this.changes.length > 64) this.changes.shift();
    }
  }
}
